import { combineReducers, configureStore } from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // localStorage

import authReducer from './authSlice';
import mediaReducer from './mediaSlice';
import favouritesReducer from './favouritesSlice';
import collectionsReducer from './collectionsSlice';
import uploadReducer from './uploadSlice';
import searchReducer from './searchSlice';
import quotaReducer from './quotaSlice';
import shareReducer from './shareSlice';
import storageTierReducer from './storageTierSlice';
import faceCollectionReducer from './faceCollectionSlice';
import sharepointReducer from './sharepointSlice';
import folderReducer from './folderSlice';
import uiReducer from './uiSlice';

// Persist uploadSlice so in-progress multipart uploads survive page refreshes
const uploadPersistConfig = {
  key: 'upload',
  storage,
  // Only persist the uploads map (not the panel open state)
  whitelist: ['uploads'],
};

// Persist uiSlice so the user's view mode preference survives page refreshes (Req 19.2)
const uiPersistConfig = {
  key: 'ui',
  storage,
  whitelist: ['viewMode'],
};

const rootReducer = combineReducers({
  auth: authReducer,
  media: mediaReducer,
  favourites: favouritesReducer,
  collections: collectionsReducer,
  upload: persistReducer(uploadPersistConfig, uploadReducer),
  search: searchReducer,
  quota: quotaReducer,
  share: shareReducer,
  storageTier: storageTierReducer,
  faceCollection: faceCollectionReducer,
  sharepoint: sharepointReducer,
  folders: folderReducer,
  ui: persistReducer(uiPersistConfig, uiReducer),
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // redux-persist dispatches non-serializable actions internally; ignore them
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
